import { useState, useEffect, useCallback, useRef } from "react";
import { useConfig } from "../../hooks/useConfig";
import { Checkbox, SettingsPanel } from "../../shared";
import { SettingsHeader } from ".";
import { getStages } from "../Jobs/job-stages";
import { ConfirmResetModal } from "../../modals";

// Simple color picker component
const ColorPicker = ({ label, value, onChange, disabled = false }) => {
  return (
    <div className="flex items-center space-x-3">
      <label className="text-sm font-medium text-gray-700 w-24 flex-shrink-0">
        {label}
      </label>
      <div className="flex items-center space-x-2">
        <input
          type="color"
          value={value || "#38bc8a"}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="h-8 w-16 border border-gray-300 rounded cursor-pointer disabled:cursor-not-allowed"
        />
        <span className="text-xs text-gray-500 font-mono">{value}</span>
      </div>
    </div>
  );
};

const Dispatch = () => {
  const { addConfig, configs, appType } = useConfig();
  const [loading, setLoading] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get current dispatch config or set defaults
  const dispatchConfig = configs?.features?.Dispatch || {};
  const statusColors = dispatchConfig.statusColors || {};
  const timeline = dispatchConfig.timeline || {};
  const themes = dispatchConfig.themes || {};
  const timingAlerts = dispatchConfig.timingAlerts || {};

  const [selectedTheme, setSelectedTheme] = useState(
    themes.default || "standard"
  );

  const [colorConfig, setColorConfig] = useState(statusColors);
  const [timelineConfig, setTimelineConfig] = useState({
    workHours: timeline.workHours || { start: 8, end: 18 },
  });
  const [timingAlertsConfig, setTimingAlertsConfig] = useState({
    enabled: timingAlerts.enabled !== false,
    allowPerAdminHide: timingAlerts.allowPerAdminHide !== false,
  });

  // Debouncing refs
  const colorSaveTimeoutRef = useRef(null);
  const timelineSaveTimeoutRef = useRef(null);

  // Get actual job statuses from job-stages.tsx
  const getJobStatuses = () => {
    const stages = getStages(appType) || getStages("serviceRepair");
    return Object.values(stages).map((stage) => ({
      id: stage.id,
      title: stage.title,
      defaultColor: getDefaultColorForStatus(stage.id),
    }));
  };

  // Map default colors based on the color property in job-stages.tsx
  const getDefaultColorForStatus = (statusId) => {
    const colorMap = {
      new: "#38bc8a",
      pending: "#f59e0b",
      scheduled: "#f59e0b",
      booked: "#3b82f6",
      assigned: "#eab308",
      dispatched: "#eab308",
      enRoute: "#3b82f6",
      inProgress: "#06b6d4",
      in_progress: "#06b6d4",
      delivered: "#06b6d4",
      completed: "#10b981",
      invoiced: "#8b5cf6",
      paid: "#10b981",
    };
    return colorMap[statusId] || "#38bc8a";
  };

  // Helper function to convert 24-hour format to 12-hour AM/PM format
  const formatTime12Hour = (hour) => {
    if (hour === 0) return "12:00 AM";
    if (hour < 12) return `${hour}:00 AM`;
    if (hour === 12) return "12:00 PM";
    return `${hour - 12}:00 PM`;
  };

  useEffect(() => {
    // Update local state when configs change
    const dispatchConfig = configs?.features?.Dispatch || {};
    const statusColors = dispatchConfig.statusColors || {};
    const timeline = dispatchConfig.timeline || {};
    const themes = dispatchConfig.themes || {};
    const timingAlerts = dispatchConfig.timingAlerts || {};

    setColorConfig(statusColors);
    setTimelineConfig({
      workHours: timeline.workHours || { start: 8, end: 18 },
    });
    setSelectedTheme(themes.default || "standard");
    setTimingAlertsConfig({
      enabled: timingAlerts.enabled !== false,
      allowPerAdminHide: timingAlerts.allowPerAdminHide !== false,
    });
  }, [configs?.features?.Dispatch]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (colorSaveTimeoutRef.current) {
        clearTimeout(colorSaveTimeoutRef.current);
      }
      if (timelineSaveTimeoutRef.current) {
        clearTimeout(timelineSaveTimeoutRef.current);
      }
    };
  }, []);

  // Debounced save functions
  const debouncedSaveColorConfig = useCallback(
    (newColorConfig) => {
      if (colorSaveTimeoutRef.current) {
        clearTimeout(colorSaveTimeoutRef.current);
      }
      colorSaveTimeoutRef.current = setTimeout(() => {
        saveColorConfig(newColorConfig);
      }, 500); // Wait 500ms after user stops changing colors
    },
    [dispatchConfig]
  );

  const debouncedSaveTimelineConfig = useCallback(
    (newTimelineConfig) => {
      if (timelineSaveTimeoutRef.current) {
        clearTimeout(timelineSaveTimeoutRef.current);
      }
      timelineSaveTimeoutRef.current = setTimeout(() => {
        saveTimelineConfig(newTimelineConfig);
      }, 500); // Wait 500ms after user stops changing timeline
    },
    [dispatchConfig]
  );

  const handleColorChange = (statusId, color) => {
    const newColorConfig = {
      ...colorConfig,
      [statusId]: color,
    };
    // Update local state immediately for responsive UI
    setColorConfig(newColorConfig);
    // Debounce the API call
    debouncedSaveColorConfig(newColorConfig);
  };

  const handleWorkHoursChange = (type, value) => {
    const newWorkHours = {
      ...timelineConfig.workHours,
      [type]: parseInt(value),
    };
    const newTimelineConfig = {
      ...timelineConfig,
      workHours: newWorkHours,
    };
    // Update local state immediately for responsive UI
    setTimelineConfig(newTimelineConfig);
    // Debounce the API call
    debouncedSaveTimelineConfig(newTimelineConfig);
  };

  const handleThemeChange = async (theme) => {
    setSelectedTheme(theme);
    setLoading(true);
    try {
      await addConfig({
        configKey: "features",
        data: {
          Dispatch: {
            ...dispatchConfig,
            themes: {
              ...themes,
              default: theme,
            },
          },
        },
        type: "object",
      });
    } catch (error) {
      console.error("Failed to save theme:", error);
    } finally {
      setLoading(false);
    }
  };

  const saveColorConfig = async (newColorConfig) => {
    setIsSaving(true);
    try {
      await addConfig({
        configKey: "features",
        data: {
          Dispatch: {
            ...dispatchConfig,
            statusColors: newColorConfig,
          },
        },
        type: "object",
      });
    } catch (error) {
      console.error("Failed to save color config:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const saveTimelineConfig = async (newTimelineConfig) => {
    setIsSaving(true);
    try {
      await addConfig({
        configKey: "features",
        data: {
          Dispatch: {
            ...dispatchConfig,
            timeline: newTimelineConfig,
          },
        },
        type: "object",
      });
    } catch (error) {
      console.error("Failed to save timeline config:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetClick = () => {
    setShowResetModal(true);
  };

  const saveTimingAlertsConfig = async (newTimingAlertsConfig) => {
    setIsSaving(true);
    try {
      await addConfig({
        configKey: "features",
        data: {
          Dispatch: {
            ...dispatchConfig,
            timingAlerts: newTimingAlertsConfig,
          },
        },
        type: "object",
      });
    } catch (error) {
      console.error("Failed to save timing alert config:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleTimingAlertToggle = async (key, value) => {
    const newTimingAlertsConfig = {
      ...timingAlertsConfig,
      [key]: value,
    };

    if (key === "enabled" && value === false) {
      newTimingAlertsConfig.allowPerAdminHide = false;
    }

    if (key === "enabled" && value === true) {
      newTimingAlertsConfig.allowPerAdminHide =
        timingAlertsConfig.allowPerAdminHide !== false;
    }

    setTimingAlertsConfig(newTimingAlertsConfig);
    await saveTimingAlertsConfig(newTimingAlertsConfig);
  };

  const resetToDefaults = async () => {
    setLoading(true);
    try {
      // This will reset to the backend defaults
      await addConfig({
        configKey: "features",
        data: {
          Dispatch: null,
        },
        type: "object",
      });
      setShowResetModal(false);
    } catch (error) {
      console.error("Failed to reset to defaults:", error);
    } finally {
      setLoading(false);
    }
  };

  // Theme settings
  const themeSettings = () => {
    return [
      {
        title: "Color Theme",
        subtitle:
          "Choose a color theme for accessibility and preference. Themes will override individual color settings.",
        content: (
          <div className="space-y-3">
            <select
              value={selectedTheme}
              onChange={(e) => handleThemeChange(e.target.value)}
              disabled={loading}
              className="border border-gray-300 rounded px-3 py-2 text-sm w-full max-w-xs"
            >
              <option value="standard">Standard</option>
              <option value="high_contrast">High Contrast</option>
              <option value="colorblind_friendly">Colorblind Friendly</option>
            </select>
            {selectedTheme !== "standard" && (
              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded">
                Note: This theme will override the individual status colors
                below.
              </p>
            )}
          </div>
        ),
      },
    ];
  };

  // Job status color settings
  const statusColorSettings = () => {
    const jobStatuses = getJobStatuses();

    return [
      {
        title: "Job Status Colors",
        subtitle: `Set colors for different ${
          appType === "trucking" ? "trucking" : "service"
        } job statuses in the dispatch timeline`,
        content: (
          <div className="space-y-3">
            {/* {isSaving && (
              <div className="flex items-center space-x-2 text-sm text-blue-600 bg-blue-50 p-2 rounded">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <span>Saving changes...</span>
              </div>
            )} */}
            {jobStatuses.map((status) => (
              <ColorPicker
                key={status.id}
                label={status.title}
                value={colorConfig[status.id] || status.defaultColor}
                onChange={(color) => handleColorChange(status.id, color)}
                disabled={loading || selectedTheme !== "standard"}
              />
            ))}
            {selectedTheme !== "standard" && (
              <p className="text-sm text-gray-500 italic">
                Individual colors are disabled when using a theme other than
                Standard.
              </p>
            )}
          </div>
        ),
      },
    ];
  };

  // Timeline settings
  const timelineSettings = () => {
    return [
      {
        title: "Work Hours",
        subtitle: "Set the default work hours for the dispatch timeline view",
        content: (
          <div className="space-y-3">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  Start Hour:
                </label>
                <select
                  value={timelineConfig.workHours.start}
                  onChange={(e) =>
                    handleWorkHoursChange("start", e.target.value)
                  }
                  disabled={loading}
                  className="border border-gray-300 rounded px-3 py-1 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>
                      {formatTime12Hour(i)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-center space-x-2">
                <label className="text-sm font-medium text-gray-700">
                  End Hour:
                </label>
                <select
                  value={timelineConfig.workHours.end}
                  onChange={(e) => handleWorkHoursChange("end", e.target.value)}
                  disabled={loading}
                  className="border border-gray-300 rounded px-3 py-1 text-sm focus:border-primary-500 focus:ring-1 focus:ring-primary-500 focus:outline-none"
                >
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {formatTime12Hour(i + 1)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        ),
      },
    ];
  };

  const timingAlertSettings = () => {
    return [
      {
        title: "Dispatcher Timing Alerts",
        subtitle:
          "Control whether timing alerts appear in the dispatcher workflow panel.",
        content: (
          <div className="space-y-3">
            <Checkbox
              id="dispatch-timing-alerts-enabled"
              checked={timingAlertsConfig.enabled}
              disabled={loading}
              onChange={(target) =>
                handleTimingAlertToggle("enabled", target.checked)
              }
              label="Show timing alerts in dispatch"
            />
            <Checkbox
              id="dispatch-timing-alerts-admin-hide"
              checked={timingAlertsConfig.allowPerAdminHide}
              disabled={loading || !timingAlertsConfig.enabled}
              onChange={(target) =>
                handleTimingAlertToggle("allowPerAdminHide", target.checked)
              }
              label="Allow admins to hide timing alerts for themselves"
            />
            {!timingAlertsConfig.enabled && (
              <p className="text-sm text-gray-500 italic">
                Timing alerts are hidden for everyone while this setting is off.
              </p>
            )}
          </div>
        ),
      },
    ];
  };

  return (
    <>
      <SettingsHeader
        title="Dispatch & Scheduling"
        actions={
          <button
            onClick={handleResetClick}
            disabled={loading}
            className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
          >
            Reset to Defaults
          </button>
        }
      />

      <div className="space-y-6">
        {/* <SettingsPanel title="Theme Settings" data={themeSettings()} /> */}
        <SettingsPanel title="Status Colors" data={statusColorSettings()} />
        <SettingsPanel title="Timeline Settings" data={timelineSettings()} />
        <SettingsPanel title="Timing Alerts" data={timingAlertSettings()} />
      </div>

      {loading && (
        <div className="fixed top-4 right-4 bg-blue-500 text-white px-4 py-2 rounded shadow-lg">
          Saving...
        </div>
      )}

      <ConfirmResetModal
        visible={showResetModal}
        setVisible={setShowResetModal}
        onConfirm={resetToDefaults}
        loading={loading}
      />
    </>
  );
};

export default Dispatch;
